/**
 * trackTS: web-based Tracker (https://physlets.org/tracker/). Get position data from objects in a video.
 * Copyright (C) 2018 Luca Demian
 */

import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import * as XLSX from 'xlsx';
import type { Modal, ModalExportData } from './classes/modal';
import { alertModal } from './classes/modal';
import { SCALE_EDIT_FOCUS_DELAY_MS } from './constants';
import { hideLoader, showLoader } from './functions';
import {
	CUSTOM_EXTENSION,
	canvas,
	EXPORT_FORMATS,
	editProject,
	editScale,
	editTrack,
	exportData,
	master,
	newProject,
	newScale,
	newTrack,
	saveProject,
	stage,
} from './globals';
import { initGoogleApis, isGoogleApisReady } from './googleAuth';
import { fetchBinaryContent } from './load';
import { DriveUpload } from './saveDrive';

newProject.on('submit', function (this: Modal, data: ModalExportData) {
	if (!data) return;
	const videoSpeed = parseFloat(data.videospeed);
	const frameSkip = parseInt(data.frameskip, 10);
	const pointsForward = parseInt(data.pointsForward, 10);
	const pointsBackward = parseInt(data.pointsBackward, 10);
	if (
		Number.isNaN(videoSpeed) ||
		Number.isNaN(frameSkip) ||
		Number.isNaN(pointsForward) ||
		Number.isNaN(pointsBackward)
	) {
		return;
	}
	master.name = data.name;
	master.videoSpeed = videoSpeed;
	master.timeline.frameSkip = frameSkip;
	master.timeline.updateTiming(master.timeline.video.duration, data.framerate);
	master.timeline.createFrames();
	const axesPos = master.toScaled(canvas.width / 2, canvas.height / 2);
	master.newAxes(axesPos.x, axesPos.y, data.axesColor, true);
	this.hide().clear();
	master.viewPoints = {
		forward: pointsForward,
		backward: pointsBackward,
	};
	master.updateVisiblePoints();
	master.created = true;
	master.trigger('created');

	gtag('event', 'New Project', {
		event_category: 'Start',
		event_label: master.name,
	});
});

saveProject
	.on('saveFile', async function (this: Modal, modalData: ModalExportData) {
		if (!modalData) return;
		if (!master.videoFile) return;
		showLoader();
		this.hide().clear();

		try {
			const fileUrl = URL.createObjectURL(master.videoFile);
			const data = await fetchBinaryContent(fileUrl);

			let filename = modalData.filename || '';
			if (filename.length === 0) {
				filename = `${master.name.toLowerCase().replace(' ', '_')}-${Date.now()}.${CUSTOM_EXTENSION}`;
			} else if (filename.split('.').pop() !== CUSTOM_EXTENSION) filename += `.${CUSTOM_EXTENSION}`;

			const projectInfo = JSON.stringify(master.save());
			const zip = new JSZip();
			zip.file('video.mp4', data, { binary: true }).file('meta.json', projectInfo);

			const blob = await zip.generateAsync({ type: 'blob', mimeType: 'application/octet-stream' });
			saveAs(blob, filename);
			master.saved = true;
		} catch (err) {
			console.error(err);
			await alertModal('Failed to save project. Please try again.', 'Save Error');
		} finally {
			hideLoader();
		}
	})
	.on('create', function (this: typeof saveProject) {
		const button = document.getElementById(`${this.id}_button-saveDrive`) as HTMLButtonElement | null;
		if (button) button.disabled = true;
		initGoogleApis()
			.then(() => {
				if (isGoogleApisReady()) {
					const logoutEl = document.getElementById('logout-button');
					const saveDriveBtn = document.getElementById(
						`${this.id}_button-saveDrive`,
					) as HTMLButtonElement | null;
					if (saveDriveBtn && logoutEl) {
						new DriveUpload({
							buttonEl: saveDriveBtn,
							logoutEl: logoutEl,
							getFile: async (callback) => {
								if (!master.videoFile) return;
								showLoader();
								try {
									const fileUrl = URL.createObjectURL(master.videoFile);
									const data = await fetchBinaryContent(fileUrl);

									const exported = this.export();
									let filename =
										exported && typeof exported === 'object'
											? ((exported as Record<string, string>).filename ?? '')
											: '';
									if (filename.length === 0) {
										filename = `${master.name.toLowerCase().replace(' ', '_')}-${Date.now()}.${CUSTOM_EXTENSION}`;
									} else if (filename.split('.').pop() !== CUSTOM_EXTENSION)
										filename += `.${CUSTOM_EXTENSION}`;

									const projectInfo = JSON.stringify(master.save());
									const zip = new JSZip();
									zip.file('video.mp4', data, { binary: true }).file('meta.json', projectInfo);

									const zipFile = await zip.generateAsync({
										type: 'blob',
										mimeType: 'application/octet-stream',
									});
									const arrayBuffer = await zipFile.arrayBuffer();
									callback(arrayBuffer, filename, (_success: boolean) => {
										hideLoader();
										this.hide();
									});
								} catch (err) {
									console.error(err);
								}
							},
						});
					}
				}
			})
			.catch((err) => {
				console.error('Google APIs failed to initialize', err);
			});
	})
	.on('cancel', function (this: Modal) {
		this.hide().clear();
	});

editProject
	.on('submit', function (this: Modal, data: ModalExportData) {
		if (!data) return;
		const frameSkip = parseInt(data.frameskip, 10);
		const pointsForward = parseInt(data.pointsForward, 10);
		const pointsBackward = parseInt(data.pointsBackward, 10);
		if (Number.isNaN(frameSkip) || Number.isNaN(pointsForward) || Number.isNaN(pointsBackward)) {
			return;
		}
		master.name = data.name;
		master.timeline.frameSkip = frameSkip;
		master.axes?.updateColor(data.axesColor);
		this.hide().clear();
		master.viewPoints = {
			forward: pointsForward,
			backward: pointsBackward,
		};
		master.updateVisiblePoints();
	})
	.on('cancel', function (this: Modal) {
		this.hide().clear();
	});

exportData
	.on('submit', function (this: Modal, data: ModalExportData) {
		if (!data) return;
		const workbook = XLSX.utils.book_new();

		for (const uid in master.trackList) {
			const track = master.trackList[uid];
			const ws = XLSX.utils.aoa_to_sheet(track.export().table.scaled);
			XLSX.utils.book_append_sheet(workbook, ws, track.name);
		}

		let filename = data.filename;

		if (filename.split('.').length <= 1) filename += '.xlsx';

		if (!EXPORT_FORMATS.includes(filename.split('.')[filename.split('.').length - 1])) filename += '.xlsx';

		XLSX.writeFile(workbook, filename);

		this.hide().clear();
	})
	.on('cancel', function (this: Modal) {
		this.hide().clear();
	});

editScale
	.on('cancel', function (this: Modal) {
		this.hide().clear();
	})
	.on('submit', function (this: Modal, data: ModalExportData) {
		if (!data) return;
		master.scale?.updateInfo({ color: data.color });
		this.hide();
	});

let scaleClickCounter = 3;
newScale
	.on('submit', function (this: Modal, data: ModalExportData) {
		if (!data) return;
		master.state.mode = 'newScale';
		scaleClickCounter = 1;
		const locations = {
			point1: { x: 0, y: 0 },
			point2: { x: 0, y: 0 },
		};
		const posTextEl = stage.children.find((c) => c instanceof createjs.Text) as createjs.Text | undefined;
		if (posTextEl) posTextEl.text = 'Click for 1st end of scale';
		stage.on('click', (e: createjs.MouseEvent) => {
			const mouseCoords = master.toScaled(e.stageX, e.stageY);
			if (scaleClickCounter === 1) {
				locations.point1 = { x: mouseCoords.x, y: mouseCoords.y };
				if (posTextEl) posTextEl.text = 'Click for 2nd end of scale';
				scaleClickCounter++;
			} else if (scaleClickCounter === 2) {
				locations.point2 = { x: mouseCoords.x, y: mouseCoords.y };
				const scale = master.newScale(
					null,
					locations.point1.x,
					locations.point1.y,
					locations.point2.x,
					locations.point2.y,
					data.color,
				);
				window.setTimeout(() => {
					scale.textElement.dispatchEvent(new Event('startEditing'));
					scale.textElement.value = '';
					scale.textElement.focus();
				}, SCALE_EDIT_FOCUS_DELAY_MS);
				stage.cursor = 'default';
				master.state.default();
				scaleClickCounter++;
			}
		});
		this.hide().clear();
	})
	.on('cancel', function (this: Modal) {
		this.hide().clear();
	});

editTrack
	.on('cancel', function (this: Modal) {
		this.hide().clear();
	})
	.on('submit', function (this: Modal, data: ModalExportData) {
		if (!data) return;
		if (master.trackList[data.uid] !== undefined) {
			master.trackList[data.uid].update({
				name: data.name,
				color: data.color,
			});
		}
		this.hide();
	});

newTrack
	.on('cancel', function (this: Modal) {
		this.hide().clear();
	})
	.on('submit', function (this: Modal, data: ModalExportData) {
		if (!data) return;
		this.hide().clear();
		master.newTrack(data.name, data.color, true);
	});
